import {forwardRef} from 'react';

import styled from '@emotion/styled';

import theme from 'theme';
import {SizeTokenValue} from 'common';

import {SegmentedControlItem} from './SegmentedControlItem';

import type {CSSObject} from '@emotion/styled';
import type {SegmentedControlOption, SegmentedControlProps} from './types';

const Container = styled.div<Pick<SegmentedControlProps, 'size' | 'disabled' | 'readOnly' | 'width'>>`
  position: relative;
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-start;
  border-radius: 4px;
  background-color: ${theme.colors.grey[20]};

  > label > span {
    ${props => {
      const isSmall = props.size === SizeTokenValue.Small;

      const styles: CSSObject = {
        padding: isSmall ? '4px 16px' : '12px 20px',
        minHeight: isSmall ? 32 : 48,
      };

      if (props.disabled) {
        styles.borderColor = theme.colors.grey[20];
        styles.backgroundColor = theme.colors.grey[20];
        styles.color = theme.colors.grey[40];
      }

      return styles;
    }}
  }

  ${props => ({
    width: props.width,
    pointerEvents: props.disabled || props.readOnly ? 'none' : undefined,
  })}
`;

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>((props, ref) => {
  const {className, options, size, width, value, qa = 'segmented'} = props;

  const isChecked = (option: SegmentedControlOption): boolean => {
    if (typeof value === 'undefined') {
      return false;
    }

    if (Array.isArray(value)) {
      return value.includes(option.value);
    }

    return value === option.value;
  };

  const children = options.map((option, index) => {
    const resultProps = {
      option,

      onChange: props.onChange,
      name: option.name ?? props.name,
      disabled: option.disabled ?? props.disabled,
      readonly: option.readOnly ?? props.readOnly,
      checked: isChecked(option),

      qa: option.qa ?? `${qa}-element`,
    };

    return <SegmentedControlItem key={`${index}-${option.value}`} {...resultProps} />;
  });

  const containerProps = {
    size,
    width,
    children,
    className,

    disabled: props.disabled,
    readOnly: props.readOnly,

    'data-qa': qa,
  };

  return <Container ref={ref} {...containerProps} />;
});
